/* eslint-disable */
import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useOfflineQuery } from '../../Hooks/graphql_utils'
import { useLocalStorage, useOnlineUtils } from '../../Hooks/offline'
import { Icon, PopoverInteractionKind, Position } from '@blueprintjs/core'

import FileBrowser from './react-keyed-file-browser'
import { useToast } from '../../Hooks/toast'

import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { useMediaQuery } from 'react-responsive'
import { AnchorButton, Card, Col, Dialog, Popover, Row } from '../../Elements'
import { getPresitePath } from '../../utils/helpers'

import UploadForm, { uploadDocument } from './UploadForm'
import ConfirmDeletionRenderer from './ConfirmDeletionRenderer'
import DetailRenderer, { ZoomableImage } from './DetailRenderer'

import {
    ADD_DOCUMENT_TAG,
    DOCUMENT_QUERY,
    GET_PRESIGN,
    INSERT_DOCUMENT,
    REMOVE_DOCUMENT_MUTATION,
    REMOVE_DOCUMENT_TAG,
    RENAME_FILE
} from './queries'
import MultipleConfirmDeletionRenderer from './MultipleConfirmDeletionRenderer'
import ImageGallery from 'react-image-gallery'
import Actions from './ActionRenderer'
import "./index.css"


const docStyle = {
    paddingRight: '10px'
}

const defaultFolderKeys = ['Plan/', 'Presite/', 'Gunite/', 'Warranty/', 'Estimate/', 'Estimate/Email/',
    'Pebble/', 'Pebble/Prep/', 'Pebble/App/', 'Pebble/Detail/', 'Pebble/Slurry/', 'Pebble/Other/', 'Request Form/']

const estimateFolderKeys = ['Plan/', 'Estimate/', 'Estimate/Email/']

const UpdatePresiteDocumentCount = (values, documents) => {
    if (values?.sub_jobs) {
        values.sub_jobs.forEach(sub_job => {
            const path = getPresitePath(sub_job)
            if (sub_job?.presite) {
                sub_job.presite.document_count = documents.filter(d => d.file_name.startsWith(path)).length
            }
        })
    }
}

export default ({ values, openFolder, isEstimate, documentTags, innerKey, ...props }) => {
    return (
        <>
            <DndProvider backend={HTML5Backend}>
                <DocumentTree
                    values={values}
                    openFolder={openFolder}
                    isEstimate={isEstimate}
                    documentTags={documentTags}
                    key={innerKey}
                    {...props}
                >
                </DocumentTree>
            </DndProvider>

        </>
    )
}

const DocumentTree = ({ values, openFolder, isEstimate, documentTags }) => {
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
    const [documentData, setDocumentData] = useState([])
    const { showError } = useToast()
    const [getPresign] = useMutation(GET_PRESIGN)
    const [insertDocuments] = useMutation(INSERT_DOCUMENT)
    const [renameDocument] = useMutation(RENAME_FILE)
    const [addDocumentTag] = useMutation(ADD_DOCUMENT_TAG)
    const [removeDocumentTag] = useMutation(REMOVE_DOCUMENT_TAG)
    const [removeDocument] = useMutation(REMOVE_DOCUMENT_MUTATION)
    const [failedFiles, setFailedFiles] = useState([])
    const [isGalleryDialogOpen, setIsGalleryDialogOpen] = useState(false)
    const { isOnline } = useOnlineUtils()
    const { setMutatedStorage, localState } = useLocalStorage()
    const [selectedFolder, setSelectedFolder] = useState("")

    const handleOnSelectFile = (key) => {
        const splitFileKey = key.split('/')
        const fileName = splitFileKey.pop()
        const filesFolderKey = splitFileKey.join('/')

        setSelectedFolder(filesFolderKey)
    }

    const getFolderCounts = (documentData) => {
        let folderCounts = {}

        documentData.forEach(k => {
            const regex = /[^\/]*$/
            const key = k.key.replace(regex, '')
            const count = documentData.filter(d => d.key.startsWith(key) && d.document_id).length
            folderCounts[key] = count
        })

        return folderCounts
    }

    const getRelativeKeys = (documentData, presite) => {
        const defaultFolders = (isEstimate ? estimateFolderKeys : defaultFolderKeys).map(k => ({
            key: k,
            url: null
        }))

        const presiteFolders = presite.map(k => ({
            key: `Presite/${k.name}/`,
            url: null
        }))

        return defaultFolders.concat(presiteFolders).concat(documentData?.map(d => {
            return {
                document_id: d.id,
                key: d.file_name,
                url: d.document_url,
                modified: new Date(d.upload_date?.replace(/-/g, "/")), //need to replace the hyphens with slashes for safari_iOS date parsing
                document_tags: d.document_tags
            }
        }))
    }

    const presite = getPresiteDetails(values.sub_jobs)

    const isMobile = useMediaQuery({ query: '(max-width: 800px)' })

    const { loading } = useOfflineQuery(DOCUMENT_QUERY, {
        variables: {
            job_id: values.id || 0
        },
        notifyOnNetworkStatusChange: true,
        onCompleted: (data => {
            setDocumentData(getRelativeKeys(data.documents, presite))
            UpdatePresiteDocumentCount(values, data.documents)
        })
    })

    const handleDeleteFolder = (folderKey) => {
        if (Array.isArray(folderKey) && folderKey.length === 0) {
            return
        }
        let keysToDelete = []

        if (Array.isArray(folderKey)) {
            folderKey.forEach(key => {
                let keys = documentData.filter(document => (document.key.startsWith(key)) && document.document_id)
                keysToDelete = keysToDelete.concat(keys)
            })
        } else {
            keysToDelete = documentData.filter(document => (document.key.startsWith(folderKey)) && document.document_id)
        }

        if (keysToDelete.length === 0) {
            let theData = documentData.filter(d => !d.key.startsWith(folderKey))
            setDocumentData(theData)
        }

        keysToDelete.forEach(async theDoc => {
            await removeDocument({
                variables: {
                    id: theDoc.document_id,
                    documentKey: theDoc.key,
                    jobId: values.id
                }
            }).then((res) => {
                setDocumentData(getRelativeKeys(res.data.removeDocument, presite))
            })
        })
    }

    const handleDeleteFile = (fileKey) => {
        fileKey.forEach(async key => {
            const theDoc = documentData.find(d => d.key === key)
            const keySuffix = theDoc?.url?.split('/job/')?.[1]?.split('?AWSAccess')?.[0] || ''
            if (keySuffix) {
                removeDocument({
                    variables: {
                        id: theDoc.document_id,
                        documentKey: keySuffix ? 'job/' + keySuffix : theDoc.key,
                        jobId: values.id
                    }
                }).then((res) => {
                    setDocumentData(currData => getRelativeKeys(res.data.removeDocument, presite).concat(currData.filter(i => i.isBeingCreated && i.relativeId !== key)))
                })
            }
        })
    }

    const deleteFailedUploadFile = ({ fullKey = null }) => {
        removeDocument({
            variables: {
                id: 0,
                documentKey: fullKey,
                jobId: values.id
            }
        })
    }

    const handleCreateFolder = (key) => {
        let newData = [...documentData]
        newData.push({ key: key, url: null })

        setDocumentData(newData)
    }

    const handleRenameFolder = (oldKey, newKey) => {
        let newData = []
        documentData.forEach(async d => {
            if (d.key.startsWith(oldKey)) {
                newData.push({
                    ...d,
                    key: d.key.replace(oldKey, newKey),
                    modified: new Date()
                })
                if (d.document_id) {
                    await renameDocument({
                        variables: {
                            document_id: d.document_id,
                            job_id: values.id,
                            newName: d.key.replace(oldKey, newKey)
                        }
                    })
                }

            } else {
                newData.push(d)
            }
        })

        setDocumentData(newData)
    }

    const handleRenameFile = (oldKey, newKey) => {
        let newData = []
        let newKeyExists = documentData.find(d => d.key === newKey)

        if (newKeyExists) {
            return
        }

        documentData.forEach(async (d) => {
            if (d.key === oldKey) {
                newData.push({
                    ...d,
                    key: d.key.replace(oldKey, newKey),
                    modified: new Date()
                })
                await renameDocument({
                    variables: {
                        document_id: d.document_id,
                        job_id: values.id,
                        newName: `${newKey}`
                    }
                })
            } else {
                newData.push(d)
            }
        })

        setDocumentData(newData)
    }

    const handleAddTag = async (documentId, tagId) => {
        await addDocumentTag({
            variables: {
                documentId,
                tagId
            }
        }).then((res) => {
            const newData = documentData.map(d => {
                return {
                    ...d,
                    document_tags: d?.document_id !== documentId
                        ? d.document_tags
                        : d.document_tags.concat([documentTags.find(t => t.id === tagId)]),
                    modified: d?.document_id !== documentId
                        ? d.modified
                        : new Date(res.data.addDocumentTag.upload_date)
                }
            })
            setDocumentData(newData)
        })
    }

    const handleRemoveTag = async (documentId, tagId) => {
        await removeDocumentTag({
            variables: {
                documentId,
                tagId
            }
        }).then((res) => {
            const newData = documentData.map(d => {
                return {
                    ...d,
                    document_tags: d?.document_id !== documentId
                        ? d.document_tags
                        : d.document_tags.filter(t => t.id !== tagId),
                    modified: d?.document_id !== documentId
                        ? d.modified
                        : new Date(res.data.removeDocumentTag.upload_date)
                }
            })
            setDocumentData(newData)
        })
    }

    const handleCreateFiles = (files, prefix) => {
        let file_list = []
        Array.from(files).forEach(
            file_data => {
                const fileExists = documentData.find(f => f.key == `${prefix}${file_data.name}`)

                if (!fileExists) {
                    file_list.push({
                        file_data
                    })
                }
            }
        )

        if (!isOnline) {
            let uploadPromises = []
            file_list.forEach(d => {

                const promise = new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(d.file_data);
                    reader.onload = () => resolve((
                        {
                            key: `job/${values.id}/documents/${prefix}${d.file_data.name}`,
                            document_url: reader.result
                        }
                    ));
                    reader.onerror = error => reject(error);
                });
                uploadPromises.push(promise)
            })
            Promise.all(uploadPromises).then(async (promiseValues) => {
                const mappedModel = {
                    job_id: values.id,
                    document_file_list: file_list.map(d => {
                        const found_data = promiseValues.find(v => v.key === `job/${values.id}/documents/${prefix}${d.file_data.name}`)
                        return ({
                            job_type_job_category: null,
                            file_name: `${prefix}${d.file_data.name}`,
                            file_type: d.file_data.type,
                            key: `job/${values.id}/documents/${prefix}${d.file_data.name}`,
                            document_url: found_data && found_data.document_url,
                            type: d.file_data.type,
                            upload_date: new Date().toString(),
                            job_id: values.id
                        })
                    })
                }

                let documents = localState['documents'] || []
                documents.push(...mappedModel.document_file_list)

                //setMutatedStorage('documents', documents)
                setDocumentData(getRelativeKeys(documents, presite))

            })
        }

        if (isOnline) {
            file_list.map(d => uploadDocument(values.id, d, getPresign, insertDocuments, setDocumentData, getRelativeKeys, presite, deleteFailedUploadFile, setFailedFiles, prefix))
        }
    }

    const canViewGalley = !selectedFolder || documentData.filter(d => d.document_id && d.key?.includes(selectedFolder)).length > 0
    const imageGalleryRef = React.createRef('imgGalleryRef')
    const countOfFilesCreating = documentData?.filter(d => d.isBeingCreated).length || 0

    return (
        <>
            {isUploadDialogOpen && <UploadForm
                jobId={values.id}
                jobValues={values}
                presiteOptions={presite}
                documentTags={documentTags}
                existingFiles={documentData}
                setFailedFiles={setFailedFiles}
                getRelativeKeys={getRelativeKeys}
                setDocumentData={setDocumentData}
                isUploadDialogOpen={isUploadDialogOpen}
                setIsUploadDialogOpen={setIsUploadDialogOpen}
                deleteFailedUploadFile={deleteFailedUploadFile}
            />
            }

            <Dialog
                icon='media'
                title='View Images'
                isOpen={isGalleryDialogOpen}
                canOutsideClickClose={true}
                canEscapeKeyClose={true}
                isCloseButtonShown={true}
                onClose={() => {
                    setIsGalleryDialogOpen(false)
                }}
                style={{
                    height: '85vh',
                    width: '-webkit-fill-available',
                    margin: "0 5%",
                    overflowY: "clip"
                }}
            >
                <ImageGallery
                    ref={imageGalleryRef}
                    items={documentData
                        .filter(d => d.key.match(/.(jpg|jpeg|png|gif|mp4|webm|ogg|mov|pdf)$/i) && d.key.startsWith(selectedFolder))
                        .map((file, idx) => {
                            return {
                                original: file.url,
                                id: file.id,
                                description: file.file_name,
                                fileKey: file.key,
                                documentIndex: idx,
                                renderItem: (item) => {
                                    return <ZoomableImage fileKey={file.key} fileUrl={file.url}
                                    //shouldResetSize={item?.documentIndex !== imageGalleryRef?.current?.getCurrentIndex()}
                                    />
                                }
                            }
                        })}
                    startIndex={0}
                    showThumbnails={false}
                    showPlayButton={false}
                    showBullets={true}
                    disableSwipe={isMobile}
                />
            </Dialog>
            <Row style={{ marginBottom: '.5em' }}>
                <Col>
                    <AnchorButton
                        text={!isMobile && 'Upload Files'}
                        icon='upload'
                        intent='primary'
                        onClick={() => setIsUploadDialogOpen(true)}
                        style={{ margin: '5px 12px 5px 20px' }}
                        disabled={values.id == null || values.id === 0} />
                </Col>
                <Col>
                    <AnchorButton
                        text={!isMobile && 'Gallery'}
                        disabled={!canViewGalley}
                        icon='media'
                        intent='primary'
                        style={{ margin: '5px 12px 5px 20px' }}
                        onClick={() => {
                            setIsGalleryDialogOpen(true)
                        }}
                    />
                </Col>

                {countOfFilesCreating > 0 &&
                    <Col className='bp3-skeleton uploading-file-counter' style={uploadErrorColStype}>
                        <span>
                            {`... Uploading ${countOfFilesCreating} File${countOfFilesCreating > 1 ? 's' : ''} ...`}
                        </span>
                    </Col>
                }
                {(countOfFilesCreating < 1 && failedFiles?.length > 0) &&
                    <Col className='uploading-file-counter' style={uploadErrorColStype}>
                        <Popover
                            content={<FailedFileList failedFiles={failedFiles} />}
                            position={Position.BOTTOM}
                            interactionKind={PopoverInteractionKind.CLICK}
                        >
                            <div style={{ cursor: 'pointer' }}>
                                <Icon
                                    icon='warning-sign' /> {` Failed to Upload ${failedFiles?.length} File${failedFiles?.length > 1 ? 's' : ''}. Please try again.`}
                            </div>
                        </Popover>
                    </Col>
                }
            </Row>
            <Row>
                <Col xs={12}>
                    <FileBrowser
                        files={documentData}
                        icons={{
                            File: <Icon icon="document" style={docStyle} />,
                            Image: <Icon icon="media" style={docStyle} />,
                            PDF: <Icon icon="document" style={docStyle} />,
                            Rename: <Icon icon="properties" style={docStyle} />,
                            Folder: <Icon icon="folder-close" style={docStyle} />,
                            FolderOpen: <Icon icon="folder-open" style={docStyle} />,
                            Delete: <Icon icon="trash" style={docStyle} />,
                            Loading: <Icon icon="refresh" style={docStyle} />,
                        }}
                        allTags={documentTags}

                        detailRenderer={DetailRenderer}
                        confirmDeletionRenderer={ConfirmDeletionRenderer}
                        confirmMultipleDeletionRenderer={MultipleConfirmDeletionRenderer}

                        onCreateFolder={handleCreateFolder}
                        onRenameFolder={handleRenameFolder}
                        onDeleteFolder={handleDeleteFolder}

                        onCreateFiles={handleCreateFiles}
                        onRenameFile={handleRenameFile}
                        onDeleteFile={handleDeleteFile}

                        onMoveFile={handleRenameFile}
                        onMoveFolder={handleRenameFolder}

                        onAddTag={handleAddTag}
                        onRemoveTag={handleRemoveTag}


                        defaultOpenFolder={openFolder}

                        actionRenderer={wrappedActionRenderer({ defaultFolderKeys: isEstimate ? estimateFolderKeys : defaultFolderKeys })}

                        onSelectFolder={({ key }) => setSelectedFolder(key)}
                        onSelectFile={({ key }) => handleOnSelectFile(key)}
                        folderRendererProps={
                            {
                                lockedFolders: isEstimate ? estimateFolderKeys : defaultFolderKeys,
                                folderCounts: getFolderCounts(documentData)
                            }
                        }
                    />
                </Col>
            </Row>
        </>
    )
}

const uploadErrorColStype = {
    flexGrow: 1,
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
}

const FailedFileList = ({ failedFiles }) => <Card style={{ margin: 0, maxHeight: '10em', overflow: 'auto' }}>
    {failedFiles?.map(f => <p>{`Could not upload ${f.name} to ${f.destination}.`}</p>)}
</Card>


const wrappedActionRenderer = (outerProps) => {
    return (props) => <Actions {...props} rootFolders={outerProps.defaultFolderKeys} />
}

const getPresiteDetails = (sub_jobs) => {
    return sub_jobs ? sub_jobs.filter(sj => sj.id && sj.job_category && sj.presite && sj.presite.checkin_date).map(jobDetail => {
        let job_types = jobDetail.job_types &&
            jobDetail.job_types.map(j => j.name).join(', ')

        let job_category = `(${jobDetail.job_category.name})`

        const isPebble = jobDetail.job_category.id === 2
        const isGunite = jobDetail.job_category.id === 1

        let date = (isGunite && jobDetail.gunite_details && jobDetail.gunite_details.finished_date)
            || (isPebble && jobDetail.pebble_details && jobDetail.pebble_details.app_date)
            || ''

        let jobDetailName = `${job_types} ${job_category} ${date}`.trim()

        return {
            id: jobDetail.id,
            name: jobDetailName
        }
    })
        : []
}
